/**
 * Reports, defined as metadata like everything else.
 *
 * The definition drives the whole screen: which filters appear, which are
 * mandatory (a report does not run until they are answered — docs/10), the
 * columns, and which of them are totalled in the pinned footer.
 *
 * The QUERY is not metadata. It lives in code on the server, because a report
 * that carries its own SQL is an injection surface and a schema-change trap.
 */
export type ReportGroup =
  | 'Management' | 'Project' | 'Operation' | 'Supply chain' | 'Inventory' | 'Tools and Equipment'
  | 'Integration'

export interface ReportFilterDef {
  key: string
  label: string
  /** `date` is one day under the filter's own key — an "As on date". */
  control: 'lookup' | 'dateRange' | 'select' | 'text' | 'checkbox' | 'date'
  required?: boolean
  lookup?: { entity: string }
  options?: ReadonlyArray<{ value: string; label: string }>
  hint?: string
  /**
   * Several values at once — projects, warehouses, categories, document types
   * (a chip picker on screen). Travels as a comma-separated list under the
   * same key; the server refuses a list for a filter that does not say this,
   * and runners read it with `listOf(p, key)` (server report-chassis).
   */
  multiple?: boolean
  /**
   * Either/or mandatory filters: every filter sharing a group name is marked
   * required until ANY one of them is answered (Purchase price records: item,
   * period or category). Enforced on the server as well as on screen.
   */
  requiredGroup?: string
  /**
   * Filters this one switches off while it has a value — choosing an item
   * disables Item category and Brand. Disabled filters are cleared on screen
   * and ignored by the server.
   */
  disables?: readonly string[]
  /**
   * The window a date range opens on. Default `fy` (financial year to date);
   * `none` leaves it empty. The others run back from today: a month, three
   * months (Document approval tracker), twelve months.
   */
  defaultPeriod?: ReportDefaultPeriod
  /**
   * A browse button beside the lookup that opens a searchable picker dialog.
   * On by default for item lookups; `false` turns it off.
   */
  browse?: boolean
  /**
   * A date range kept under keys of its own, so a report can offer two of
   * them — Approved date beside Document date (RPT-016). Default `from`/`to`.
   */
  range?: { readonly from: string; readonly to: string }
  /**
   * The value a select or date filter opens on — an option's value, or
   * `today` for a date; the server applies it when none is sent.
   */
  defaultValue?: string
  /**
   * Required only while another filter holds one of these values: Warehouse
   * when showing by warehouse, Project when showing by project (RPT-128).
   */
  requiredWhen?: { readonly key: string; readonly values: readonly string[] }
  /**
   * Shown only while another filter holds one of these values: Warehouse
   * under Show by Warehouses, Project under Show by Projects (REP3-005). A
   * hidden filter is cleared on screen and ignored by the server, like a
   * disabled one.
   */
  visibleWhen?: { readonly key: string; readonly values: readonly string[] }
  /**
   * A date range drawn as two separately labelled dates, each with its own
   * required mark — From date * with To date optional (REP2-004).
   *
   * Whether the range is wanted at all still comes from `required`,
   * `requiredWhen` or `requiredGroup`; these say which ends that means.
   * `requireFrom` / `requireTo` default to true. `toFollowsFrom` asks for the
   * To date as soon as a From date is given, whether or not the range itself
   * was wanted (Project expenses, Purchase price records).
   */
  split?: ReportDateSplit
}

export type ReportDefaultPeriod = 'lastMonth' | 'last3Months' | 'last12Months' | 'fy' | 'none'

export interface ReportDateSplit {
  readonly fromLabel: string
  readonly toLabel: string
  readonly requireFrom?: boolean
  readonly requireTo?: boolean
  readonly toFollowsFrom?: boolean
}

/**
 * What a cell opens (RPT-030). The server resolves it into `row.__links[col]`
 * = `{ route, id }`, so the screen never guesses a route.
 *
 * - `document`: `idKey` holds a document id. The server checks it against
 *   the company's documents and takes the route from the stored type, so an
 *   id that is not a document simply renders as text. `docType` restricts the
 *   link to that one type (an MR column on a PO report).
 * - `project`: `idKey` holds a project (master) id; it opens the project's
 *   own document.
 */
export interface ReportLinkDef {
  readonly to: 'document' | 'project'
  readonly idKey: string
  readonly docType?: string
}

/**
 * An amount cell that opens the documents behind that one figure (RPT-030):
 * the row is identified the way the report's own `drill` says, and the list
 * is narrowed to these document types.
 */
export interface ReportColumnDrillDef {
  readonly docTypes: readonly string[]
  readonly label?: string
  /**
   * Only the ledger links in these states — the In review GRN cell opens the
   * receipts still in review, Received the approved ones. For `line` drills.
   */
  readonly states?: readonly ('APPROVED' | 'IN_REVIEW' | 'DRAFT' | 'PRE_CLOSED')[]
}

export interface ReportColumnDef {
  key: string
  label: string
  /** `duration` values are whole minutes, shown as d / hr / min and totalled like any number. */
  /**
   * `details` is the DETAILS button on every row: it opens the report's own
   * drill for that row, whatever is clicked elsewhere (RPT-017).
   */
  type?: 'text' | 'docno' | 'date' | 'money' | 'qty' | 'rate' | 'pct' | 'status' | 'duration' | 'details'
  width?: number
  align?: 'left' | 'right'
  /** Included in the pinned footer total. */
  total?: boolean
  /** Colour-coded against zero: green favourable, red not. */
  signed?: boolean
  /** The cell is a link that opens the document or project (RPT-030). */
  link?: ReportLinkDef
  /** The cell opens the documents behind its own figure (RPT-030). */
  drill?: ReportColumnDrillDef
  /** Stays put while the rest scrolls sideways — the row's identity (RPT-028). */
  frozen?: boolean
  /** A heading over a run of adjacent columns: ESTIMATED over Qty·Rate·Amount (RPT-029). */
  group?: string
  /** Hidden until somebody adds it in the column chooser (RPT-032). */
  optional?: boolean
  /** Cannot be removed in the column chooser (RPT-032). */
  locked?: boolean
  /**
   * Left off the grid when no row of the result has a value in it — the
   * workflow steps a document type's workflow does not have (REP1-012).
   */
  hideWhenEmpty?: boolean
  /**
   * Drawn only on the first of consecutive rows sharing this row key, so the
   * rows under one parent read as a group — Regularise BOQ's BOQ cells on the
   * first estimation line of each BOQ (REP1-008). On screen only: an export
   * keeps every cell filled, so it still sorts and filters.
   */
  oncePer?: string
  /**
   * The quick filter under the header (RPT-005): `text` is a contains-match,
   * `pick` a multi-select of the values present, `false` none. Default:
   * `pick` for status columns, `text` for everything else.
   */
  filter?: 'text' | 'pick' | false
  /**
   * The value drawn as a coloured badge (RPT-042): `signed` green when
   * positive and red when negative, or always the one tone named.
   */
  badge?: 'signed' | 'good' | 'warn' | 'bad'
  /**
   * Shown as a coloured pill, the tone chosen by the cell's value: sync
   * status Completed / Pending / Stopped (INT-003), project status Ongoing.
   */
  tones?: Readonly<Record<string, ReportTone>>
}

export type ReportTone = 'good' | 'warn' | 'bad' | 'info' | 'muted'

/**
 * One tab of a report whose tabs are different tables over the same filters:
 * Detailed and Logs (INT-004), Expenses and Outstanding advances (RPT-127),
 * the Summary by purchase executive (RPT-105). The runner reads `p.view`.
 */
export interface ReportViewDef {
  readonly key: string
  readonly label: string
  /** This tab's own columns; omitted, the report's. */
  readonly columns?: readonly ReportColumnDef[]
}

/** One figure on a summary card: a cell of the summary rows. */
export interface ReportSummaryMetric {
  readonly label: string
  /** The column to read. */
  readonly key: string
  /** Which row: the one whose `rowKey` column equals this. Omitted: the first row. */
  readonly row?: string
  readonly type?: ReportColumnDef['type']
  readonly signed?: boolean
  /**
   * A second figure as a small chip beside this one: the order value beside
   * the project value (REP1-010), the variance % beside the variance (REP1-011).
   */
  readonly chip?: { readonly key: string; readonly type?: ReportColumnDef['type']; readonly signed?: boolean }
  /**
   * A bar under the figure measuring it against a target: total cost against
   * the estimated cost, with "76.713% (₹4,19,268.20)" on the left and the
   * target on the right (REP1-006). `pctKey` is the share already worked out.
   */
  readonly progress?: { readonly pctKey: string; readonly targetKey: string }
}

/**
 * The Summary view as cards rather than a table (RPT-036).
 *
 * Either fixed cards that each pick figures out of the summary rows, or one
 * card per row (`cardPerRow`), titled by a column and showing the others.
 * `breakup` also draws the rows as a table under the cards.
 */
export interface ReportSummaryDef {
  readonly layout: 'cards'
  readonly rowKey?: string
  /** An empty title draws the card without a heading (Cost variance's three tiles). */
  readonly cards?: ReadonlyArray<{ readonly title: string; readonly metrics: readonly ReportSummaryMetric[] }>
  readonly cardPerRow?: { readonly titleKey: string; readonly metrics?: readonly string[] }
  readonly breakup?: boolean
  /** The heading over the breakup table: Cost variance - breakup. */
  readonly breakupTitle?: string
  /**
   * The breakup table's own columns, where they are not the Detailed view's
   * (Cost variance: by type, against per estimation line).
   */
  readonly breakupColumns?: readonly ReportColumnDef[]
  /** The row (by `rowKey`) that only feeds the cards and is left out of the breakup table. */
  readonly totalRow?: string
  /** A donut of a few figures that make up a whole (Billing reconciliation's value). */
  readonly donut?: {
    readonly title: string
    readonly row?: string
    readonly segments: ReadonlyArray<{ readonly label: string; readonly key: string }>
  }
}

/**
 * What a row on this report drills into (docs/10 — the `DETAILS` modal).
 *
 * A total on a report is only trustworthy if you can open it and see the
 * documents underneath. `by` names the column on the row that identifies the
 * thing to drill on, and `docTypes` bounds what is listed — a cost row drills
 * into invoices, not into the material requests behind them.
 */
export interface ReportDrillDef {
  /**
   * The row column holding the id: `id`, `projectId`, `itemId`, `supplierId`.
   * `line` is a document line: the drill follows the allocation ledger from it
   * to everything raised against it — the orders, receipts, transfers and
   * invoices behind an execution report's row (RPT-017, RPT-095).
   */
  readonly by: 'project' | 'item' | 'supplier' | 'document' | 'line'
  readonly key: string
  readonly docTypes?: readonly string[]
  /** What the modal is called. */
  readonly label?: string
  /**
   * The modal's own columns, in order, where the generic document list does
   * not fit the reference (REP2-006, REP2-007). Declared columns also drop the
   * money total: these modals count quantities.
   */
  readonly columns?: readonly ReportDrillColumn[]
}

/** One column of a Details modal that declares its own (see ReportDrillDef.columns). */
export interface ReportDrillColumn {
  readonly key: 'docTypeLabel' | 'documentNo' | 'documentStatus' | 'documentDate' | 'reference' | 'partyName'
    | 'itemName' | 'quantity' | 'completedQty' | 'invoicedQty' | 'amount'
  readonly label: string
}

export interface ReportDef {
  key: string
  route: string
  group: ReportGroup
  label: string
  blurb: string
  filters: readonly ReportFilterDef[]
  columns: readonly ReportColumnDef[]
  /** Most financial reports offer both; the query decides what changes. */
  hasSummaryDetail?: boolean
  /** Shown above the table when the numbers need a caveat. */
  caveat?: string
  /** Clicking a row opens the documents behind it. */
  drill?: ReportDrillDef
  /**
   * How the figures are worked out, opened from the book button on the
   * toolbar (RPT-006). Plain text; a blank line starts a new paragraph.
   */
  help?: string
  /** Or a link to a fuller help document. */
  helpUrl?: string
  /** The Summary view's card layout (RPT-036). */
  summary?: ReportSummaryDef
  /**
   * Whether it runs as soon as it is opened. Default: only when it has no
   * mandatory filters (RPT-024) — or when it is re-opened with the filters
   * last applied (RPT-072).
   */
  runOnOpen?: boolean
  /**
   * The row column whose value tones the whole row: `alert` or `warning`
   * (Regularise BOQ's overruns).
   */
  rowTone?: string
  /** Tabs that are different tables (see ReportViewDef); replaces Summary / Detailed. */
  views?: readonly ReportViewDef[]
  /**
   * The filter whose value picks the view, when the reference asks for it as a
   * filter (Material progress: Mode * Summary / Detailed) rather than as tabs.
   */
  viewBy?: string
}


/**
 * Something about these figures the reader must know, decided by the data
 * rather than printed on every run (RPT-050) — optionally pointing at the
 * report that resolves it (RPT-007: Regularise BOQ).
 */
export interface ReportWarning {
  readonly tone: 'warn' | 'info'
  readonly text: string
  /** Another report's route, opened with the same project. */
  readonly report?: string
  readonly reportLabel?: string
}

/** One labelled figure in a Details modal. */
export interface ReportDetailFact {
  readonly label: string
  readonly value: string | null
  readonly type?: ReportColumnDef['type']
}

/** One table in a Details modal; several are drawn as tabs. */
export interface ReportDetailTab {
  readonly key: string
  readonly label: string
  readonly columns: readonly ReportColumnDef[]
  readonly rows: ReadonlyArray<Record<string, unknown>>
  readonly totals?: Readonly<Record<string, string>>
}

/**
 * What a report-specific Details modal adds to the plain document list:
 * a header of facts, blocks of figures, the formula the figure came from, and
 * tabs of documents (RPT-009, RPT-013, RPT-058).
 */
export interface ReportDetailExtras {
  /** What the row is, above everything else: BOQ, Item, Design qty (RPT-017, RPT-009). */
  readonly header?: ReadonlyArray<{ readonly label: string; readonly value: string }>
  readonly blocks?: ReadonlyArray<{ readonly title: string; readonly facts: readonly ReportDetailFact[] }>
  readonly formula?: string
  readonly tabs?: readonly ReportDetailTab[]
}


/** The columns a report shows under one view. */
export const columnsForView = (def: Pick<ReportDef, 'columns' | 'views'>, view?: string): readonly ReportColumnDef[] => {
  if (!def.views?.length) return def.columns
  const chosen = def.views.find((v) => v.key === view) ?? def.views[0]
  return chosen?.columns ?? def.columns
}

/**
 * What the screen echoes above the results (RPT-035): the applied project
 * and its stage.
 */
export interface ReportContext {
  readonly project?: {
    readonly id: string
    readonly code: string
    readonly name: string
    readonly stage: string | null
    readonly ongoing: boolean
    /** The project document, to open it. */
    readonly documentId: string | null
  }
}

/** A resolved cell link — see ReportLinkDef. */
export interface ReportCellLink {
  readonly route: string
  readonly id: string
}

/** Where resolved links travel on a row. */
export const REPORT_LINKS_KEY = '__links'

export const defineReport = <const T extends ReportDef>(def: T): T => def

export const rf = {
  project: (required = false): ReportFilterDef =>
    ({ key: 'projectId', label: 'Project', control: 'lookup', required, lookup: { entity: 'project' } }),
  period: (label = 'Period', required = true): ReportFilterDef =>
    ({ key: 'period', label, control: 'dateRange', required }),
  /**
   * A period asked as From date and To date, two fields (REP2-004). The
   * filter itself is required; `ends` says which of its dates that means.
   */
  fromTo: (ends: Omit<ReportDateSplit, 'fromLabel' | 'toLabel'> = {}, required = true): ReportFilterDef =>
    ({ key: 'period', label: 'Period', control: 'dateRange', required, split: { fromLabel: 'From date', toLabel: 'To date', ...ends } }),
  supplier: (): ReportFilterDef =>
    ({ key: 'supplierId', label: 'Supplier', control: 'lookup', lookup: { entity: 'supplier' } }),
  customer: (): ReportFilterDef =>
    ({ key: 'customerId', label: 'Customer', control: 'lookup', lookup: { entity: 'customer' } }),
  warehouse: (required = false): ReportFilterDef =>
    ({ key: 'warehouseId', label: 'Warehouse', control: 'lookup', required, lookup: { entity: 'warehouse' } }),
  jobType: (): ReportFilterDef =>
    ({ key: 'jobTypeId', label: 'Job type', control: 'lookup', lookup: { entity: 'job_type' } }),
  employee: (): ReportFilterDef =>
    ({ key: 'employeeId', label: 'Employee', control: 'lookup', lookup: { entity: 'employee' } }),
  category: (): ReportFilterDef =>
    ({ key: 'categoryId', label: 'Item category', control: 'lookup', lookup: { entity: 'item_category' } }),
  status: (): ReportFilterDef =>
    ({ key: 'documentStatus', label: 'Document status', control: 'select', options: [
      { value: 'DRAFT', label: 'Draft' }, { value: 'IN_REVIEW', label: 'In review' },
      { value: 'APPROVED', label: 'Approved' }, { value: 'CANCELLED', label: 'Cancelled' },
    ] }),
  /** Document type, from the document registry; several at once. */
  docType: (options: ReadonlyArray<{ value: string; label: string }>, multiple = true): ReportFilterDef =>
    ({ key: 'docType', label: 'Document type', control: 'select', options, multiple }),
  includeSub: (): ReportFilterDef =>
    ({ key: 'includeSubProjects', label: 'Include sub project', control: 'checkbox' }),
  /** The figures as they stood at the end of a day; opens on today (RPT-002). */
  asOn: (): ReportFilterDef =>
    ({ key: 'asOn', label: 'As on date', control: 'date', defaultValue: 'today' }),
  /**
   * How a stock report is grouped (docs/10 §Inventory).
   *
   * The same stock read three ways: by where it is held, by the job it is held
   * for, or by the material itself — which is the one that answers "how much of
   * this do we have anywhere".
   */
  mode: (options: ReadonlyArray<[string, string]>, required = false): ReportFilterDef => ({
    key: 'mode', label: 'Show by', control: 'select', required,
    options: options.map(([value, label]) => ({ value, label })),
  }),
}

export const rc = {
  /** A document number that opens its document when the row carries `id` (+ `docType`). */
  doc: (key = 'documentNo', label = 'Document no', idKey = 'id'): ReportColumnDef =>
    ({ key, label, type: 'docno', width: 158, link: { to: 'document', idKey } }),
  /** A project name or code that opens the project. */
  project: (key = 'projectName', label = 'Project', idKey = 'projectId'): ReportColumnDef =>
    ({ key, label, type: 'text', width: 220, link: { to: 'project', idKey } }),
  date: (key: string, label: string): ReportColumnDef => ({ key, label, type: 'date', width: 130 }),
  text: (key: string, label: string, width?: number): ReportColumnDef => ({ key, label, type: 'text', ...(width ? { width } : {}) }),
  money: (key: string, label: string, total = true): ReportColumnDef =>
    ({ key, label, type: 'money', width: 150, align: 'right', total }),
  signed: (key: string, label: string): ReportColumnDef =>
    ({ key, label, type: 'money', width: 150, align: 'right', total: true, signed: true }),
  qty: (key: string, label: string, total = false): ReportColumnDef =>
    ({ key, label, type: 'qty', width: 118, align: 'right', total }),
  pct: (key: string, label: string): ReportColumnDef => ({ key, label, type: 'pct', width: 110, align: 'right' }),
  status: (key = 'documentStatus', label = 'Status'): ReportColumnDef => ({ key, label, type: 'status', width: 136 }),
}
